import {
  Button,
  DialogActions, FormHelperText,
  Grid,
  MenuItem,
  Typography
} from '@mui/material';
import { Field, FieldArray, Form, Formik } from 'formik';
import React from 'react';

import {
  ImageInput,
  PostContentEditor,
  SelectInput,
  TextInput,
} from '@/components';
import { SwitchInput } from '@/components/inputs/SwitchInput';
import { capitalize } from '@/utils';

import { categories, defaultInitialValues, postLang } from './constants';
import { postFormSchema } from './schemas';
import { styles } from './styles';


// import type { IFormProps } from '@/types';

type PostFormValuesProps = typeof defaultInitialValues & {
  content: {
    type: string;
    data: any;
  }[];
};

const PostForm: React.FC<any> = ({
  initialValues = defaultInitialValues,
  onSubmit,
  isLoading,
}) => {
  return (
    <Formik<PostFormValuesProps>
      enableReinitialize
      validationSchema={postFormSchema}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {({ dirty, values, errors, touched, handleChange }) => {
        return (
          <Form>
            <Grid container spacing={2} sx={styles.container}>
              <Grid item xs>
                <SwitchInput
                  placeholder="No index (hide from users)"
                  onChange={handleChange}
                  name="noIndex"
                  checked={values?.['noIndex']}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={styles.container}>
              <Grid item xs>
                <Field
                  type="text"
                  name="slug"
                  autoComplete="slug"
                  fullWidth
                  component={TextInput}
                  placeholder="Slug"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={styles.container}>
              <Grid item xs>
                <Field
                  type="text"
                  name="author"
                  autoComplete="author"
                  fullWidth
                  component={TextInput}
                  placeholder="Author"
                />
              </Grid>
              <Grid item xs>
                <Field
                  type="text"
                  name="authorPosition"
                  autoComplete="authorPosition"
                  fullWidth
                  component={TextInput}
                  placeholder="Author position"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={styles.container}>
              <Grid item xs>
                <Field
                  type="text"
                  name="title"
                  autoComplete="title"
                  fullWidth
                  component={TextInput}
                  placeholder="Title"
                />
              </Grid>
              <Grid item xs>
                <Field
                  name="category"
                  placeholder="Category"
                  component={SelectInput}
                  size="small"
                >
                  {categories.map((category) => (
                    <MenuItem value={category} key={category}>
                      <Typography>{capitalize(category)}</Typography>
                    </MenuItem>
                  ))}
                </Field>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={styles.container}>
              <Grid item xs>
                <Field
                  type="text"
                  name="description"
                  autoComplete="description"
                  fullWidth
                  component={TextInput}
                  placeholder="Description"
                />
              </Grid>
            </Grid>
            <Typography variant="h6" mb={1}>
              Content
            </Typography>
            <FieldArray
              name="content"
              render={(arrayHelpers) => (
                <>
                  <PostContentEditor
                    arrayHelpers={arrayHelpers}
                    content={values.content}
                  />
                  {touched.content && (
                    <FormHelperText
                      style={{marginLeft: '14px'}}
                      error={!!errors.content}
                    >
                      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                      {/* @ts-expect-error */}
                      {errors.content && errors.content[0]?.data?.content}
                    </FormHelperText>
                  )}
                </>
              )}
            />
            <Grid item md={6} xs>
              <Field
                type="file"
                name="image"
                component={ImageInput}
                placeholder="Image"
              />
            </Grid>
            <Grid item xs>
              <Field
                name="lang"
                placeholder="Lang"
                component={SelectInput}
                size="small"
              >
                {postLang.map((lang) => (
                  <MenuItem value={lang} key={lang}>
                    <Typography>{capitalize(lang)}</Typography>
                  </MenuItem>
                ))}
              </Field>
            </Grid>
            <Grid container spacing={2} sx={styles.container}>
              <Grid item xs>
                <Field
                  type="text"
                  name="quote"
                  autoComplete="quote"
                  fullWidth
                  component={TextInput}
                  placeholder="Quote"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={styles.container}>
              <Grid item xs>
                <Field
                  type="text"
                  name="metaTitle"
                  autoComplete="metaTitle"
                  fullWidth
                  component={TextInput}
                  placeholder="Meta title"
                />
              </Grid>
              <Grid item xs>
                <Field
                  type="text"
                  name="metaH1"
                  autoComplete="metaH1"
                  fullWidth
                  component={TextInput}
                  placeholder="Meta H1"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={styles.container}>
              <Grid item xs>
                <Field
                  type="text"
                  name="metaDescription"
                  autoComplete="metaDescription"
                  fullWidth
                  component={TextInput}
                  placeholder="Meta description"
                />
              </Grid>
              <Grid item xs>
                <Field
                  type="text"
                  name="metaKeywords"
                  autoComplete="metaKeywords"
                  fullWidth
                  component={TextInput}
                  placeholder="Meta keywords"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={styles.container}>
              <Grid item xs>
                <Field
                  type="text"
                  name="metaSubTitle"
                  autoComplete="metaSubTitle"
                  fullWidth
                  component={TextInput}
                  placeholder="Sub title for cover image"
                />
              </Grid>
            </Grid>
            <DialogActions>
              <Button
                variant="contained"
                type="submit"
                disabled={isLoading || !dirty}
                sx={styles.btn}
              >
                Save
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );
};

export default PostForm;
